/**
 * FlagEditor — the trajectories table's write surface for a run's good/bad
 * flag (TRA-963).
 *
 * The Flag cell shows the state, the verdict and the comment; clicking it
 * opens this popover to change any of them without leaving the page:
 *
 * - Save writes the verdict + comment as `review_status='reviewed'`. A
 *   human just made the call, so 'reviewed' is what it is — the create
 *   path's 'unreviewed' default would inject the run into the review queue
 *   as if a validator had flagged it.
 * - Dismiss keeps the flag as a record but takes it out of the working
 *   views, the same upsert the review composer's Dismiss writes.
 * - Remove throws the flag away (tags survive it, server-side).
 *
 * Writes target the turn the flag LIVES on, not the run's representative
 * trace: a conversation's verdict can sit on any turn. A run with no flag
 * yet is flagged on the representative trace instead.
 *
 * Layout is a form, not a toolbar: verdict and comment are labeled fields,
 * Dismiss sits with the verdict pills as the other call you can make about
 * the run, and the bottom row holds Remove plus the single solid-ink Save.
 */

import { AlertTriangle, ExternalLink } from 'lucide-react'
import { useId, useState } from 'react'
import { toast } from 'sonner'

import { flagHeading, parseVerdictSummary } from '@/components/review/flagSource'
import { evalDiverged, evalVerdict } from '@/components/trajectories/flag'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { useRemoveFlag, useUpsertLabel } from '@/hooks/useLabels'
import { isPrLink, prLabel } from '@/lib/pr'
import type { RunFlag } from '@/api/generated/model'

type Verdict = 'good' | 'bad'

interface FlagEditorProps {
  /** The run's flag, or null when nobody has flagged it yet. */
  flag: RunFlag | null
  /**
   * The run's representative trace — where a NEW flag lands. Null for a
   * conversation whose turns all fell outside the scanned log window; with
   * no flag either, there is nothing to write to and the trigger is inert.
   */
  traceId: string | null
  traceKey: string | null
  /** The cell content — chips and comment, rendered by the table. */
  children: React.ReactNode
}

export function FlagEditor({
  flag,
  traceId,
  traceKey,
  children,
}: FlagEditorProps): React.JSX.Element {
  const [open, setOpen] = useState(false)
  const notesId = useId()
  const verdictId = useId()
  // Seeded from the flag: a reviewed row re-opens on its saved verdict, an
  // unreviewed one on the judge's lean (its `approved` is "nobody signed
  // off yet", not "this run was bad"). Null on an unflagged run — Save
  // stays disabled until someone actually picks a side.
  const parsed = flag === null ? null : parseVerdictSummary(flag.notes)
  const [verdict, setVerdict] = useState<Verdict | null>(() => {
    if (flag === null) return null
    if (flag.review_status === 'reviewed') return flag.approved ? 'good' : 'bad'
    return parsed?.lean ?? null
  })
  const [notes, setNotes] = useState(parsed?.text ?? '')
  const [version, setVersion] = useState(flag?.updated_at ?? null)
  // Remove is a two-step: it discards feedback that may already have
  // produced a benchmark test, and a popover click is a cheap way to lose it.
  const [confirmingRemove, setConfirmingRemove] = useState(false)
  const upsert = useUpsertLabel()
  const remove = useRemoveFlag()

  // The flag's own turn owns the write; a new flag lands on the run's
  // representative trace.
  const targetId = flag?.trace_id ?? traceId
  const targetKey = flag?.trace_key ?? traceKey
  const writable = targetId !== null && targetKey !== null
  const pending = upsert.isPending || remove.isPending

  function close(): void {
    setConfirmingRemove(false)
    setOpen(false)
  }

  async function save(reviewStatus: 'reviewed' | 'dismissed'): Promise<void> {
    if (!writable) return
    if (reviewStatus === 'reviewed' && verdict === null) return
    try {
      const saved = await upsert.mutateAsync({
        data: {
          trace_id: targetId,
          trace_key: targetKey,
          // A dismissal makes no claim about the run, so it must not leave
          // a stale `approved: true` behind for the eval-PR agent to read.
          approved: reviewStatus === 'reviewed' && verdict === 'good',
          notes,
          expected_updated_at: version,
          review_status: reviewStatus,
        },
      })
      if (saved.status !== 200) throw new Error('Flag save returned no label')
      setVersion(saved.data.updated_at)
      close()
    } catch (error) {
      const verb = reviewStatus === 'dismissed' ? 'dismiss' : 'save'
      toast.error(`Failed to ${verb} flag: ${String(error)}`)
    }
  }

  async function handleRemove(): Promise<void> {
    if (flag === null) return
    try {
      await remove.mutateAsync({ traceId: flag.trace_id })
      close()
    } catch (error) {
      toast.error(`Failed to remove flag: ${String(error)}`)
    }
  }

  const diverged = flag !== null && evalDiverged(flag)

  function pillClass(active: boolean, tone: Verdict): string {
    if (!active) return 'border border-transparent bg-muted text-muted-foreground'
    return tone === 'bad'
      ? 'border border-destructive/30 bg-destructive/10 text-destructive'
      : 'border border-success/30 bg-success/10 text-success'
  }

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setConfirmingRemove(false)
        setOpen(next)
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={!writable}
          aria-label={flag === null ? 'Flag this run' : 'Edit flag'}
          onClick={(event) => {
            event.stopPropagation()
          }}
          className="flex w-full flex-col items-start gap-1 text-left disabled:cursor-default"
        >
          {children}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-80 p-3"
        onClick={(event) => {
          event.stopPropagation()
        }}
      >
        <div className="space-y-3">
          <div>
            <h3 className="font-serif text-sm text-foreground">
              {flag === null
                ? 'Flag this run'
                : flagHeading(flag.created_by, flag.notes)}
            </h3>
            {/* Provenance on one line: state, when, and the PR this flag
                produced. The PR link used to sit in its own block, which
                made the panel read as a stack of loose controls. */}
            {flag !== null && (
              <p className="mt-0.5 flex flex-wrap items-center gap-x-1.5 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                <span>{flag.review_status}</span>
                <span aria-hidden="true">·</span>
                <span>{new Date(flag.updated_at).toLocaleDateString()}</span>
                {/* pr_url is not always an address. The eval-PR agent
                    stamps a sentinel on labels it settled without opening
                    a PR, so the value says which of the two happened and
                    only one of them is a link. */}
                {flag.pr_url !== null && (
                  <>
                    <span aria-hidden="true">·</span>
                    {isPrLink(flag.pr_url) ? (
                      <a
                        href={flag.pr_url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-foreground underline-offset-2 hover:underline"
                      >
                        {prLabel(flag.pr_url)}
                        <ExternalLink aria-hidden="true" className="h-3 w-3" />
                      </a>
                    ) : (
                      <span>no pr</span>
                    )}
                  </>
                )}
              </p>
            )}
          </div>

          {diverged && (
            <p className="flex items-start gap-1.5 border border-destructive/40 bg-destructive/10 p-2 text-xs text-destructive">
              <AlertTriangle aria-hidden="true" className="mt-px h-3.5 w-3.5 shrink-0" />
              <span>
                The eval built from this run grades it {evalVerdict(flag)}. This
                flag now says {flag.approved ? 'good' : 'bad'}.
              </span>
            </p>
          )}

          {/* Verdict and comment are both FIELDS, so both wear a field's
              label. Giving the pills their own action row made the panel
              look like it had two toolbars competing for the primary
              action; there is exactly one, and it lives at the bottom. */}
          <div>
            <span
              id={verdictId}
              className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground"
            >
              Verdict
            </span>
            <div className="mt-1 flex items-center gap-1.5">
              {/* The group wraps the PILLS only — dismissing is not a
                  verdict, so it must not join the radio-like set even
                  though it shares the line. Its hairline shape (against
                  the pills' tint) is what keeps it from reading as a
                  third choice on the good/bad axis. */}
              <div
                role="group"
                aria-labelledby={verdictId}
                className="flex items-center gap-1.5"
              >
                {(['bad', 'good'] as const).map((tone) => (
                  <button
                    key={tone}
                    type="button"
                    aria-pressed={verdict === tone}
                    disabled={pending}
                    onClick={() => {
                      setVerdict(tone)
                    }}
                    className={`h-6 px-3 font-mono text-xs uppercase tracking-wider motion-safe:transition-colors disabled:opacity-50 ${pillClass(verdict === tone, tone)}`}
                  >
                    {tone}
                  </button>
                ))}
              </div>
              {flag !== null && (
                <button
                  type="button"
                  disabled={pending || flag.review_status === 'dismissed'}
                  title="This flag leaves the working views and stays on record. The nightly scan will not flag this run again."
                  onClick={() => {
                    void save('dismissed')
                  }}
                  className="h-6 border border-border bg-card px-3 font-mono text-xs uppercase tracking-wider text-muted-foreground hover:bg-muted disabled:opacity-50"
                >
                  Dismiss
                </button>
              )}
            </div>
          </div>

          <div>
            <label
              htmlFor={notesId}
              className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground"
            >
              Comment
            </label>
            <textarea
              id={notesId}
              rows={3}
              value={notes}
              disabled={pending}
              placeholder="What happened on this run?"
              onChange={(event) => {
                setNotes(event.target.value)
              }}
              className="mt-1 w-full resize-none border border-border bg-transparent p-2 text-sm leading-relaxed text-foreground outline-none focus:border-muted-foreground placeholder:text-muted-foreground/60 disabled:opacity-50"
            />
          </div>

          {/* Two things the reviewer can't see: removal is run-scoped (a
              conversation can hold a flag on several turns, and all of them
              go), and it leaves the run scannable again, so the nightly
              dream scan can flag it a second time while the run is still
              same-day. Both live in remove_flag in routers/labels.py. */}
          {confirmingRemove && (
            <p className="text-xs text-muted-foreground">
              This deletes the flag from every turn of this run. Tags stay, and
              tonight&rsquo;s scan can flag it again. Dismiss settles the run
              for good.
            </p>
          )}

          {/* ONE action row, always. Quiet hairline chip on the left,
              the single solid-ink action on the right. Confirming a
              removal swaps the row's contents rather than growing a
              second one. */}
          <div className="flex items-center gap-1.5">
            {confirmingRemove ? (
              <>
                <button
                  type="button"
                  disabled={pending}
                  onClick={() => {
                    setConfirmingRemove(false)
                  }}
                  className="h-6 border border-border bg-card px-3 font-mono text-xs uppercase tracking-wider text-muted-foreground hover:bg-muted disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={pending}
                  onClick={() => {
                    void handleRemove()
                  }}
                  className="ml-auto h-6 border border-destructive/40 bg-destructive/10 px-3 font-mono text-xs uppercase tracking-wider text-destructive hover:bg-destructive/20 disabled:opacity-50"
                >
                  {remove.isPending ? 'Removing…' : 'Remove flag'}
                </button>
              </>
            ) : (
              <>
                {flag !== null && (
                  <button
                    type="button"
                    disabled={pending}
                    onClick={() => {
                      setConfirmingRemove(true)
                    }}
                    className="h-6 border border-border bg-card px-3 font-mono text-xs uppercase tracking-wider text-muted-foreground hover:border-destructive/40 hover:text-destructive disabled:opacity-50"
                  >
                    Remove
                  </button>
                )}
                <button
                  type="button"
                  disabled={pending || verdict === null}
                  onClick={() => {
                    void save('reviewed')
                  }}
                  className="ml-auto h-6 bg-primary px-3.5 font-mono text-xs uppercase tracking-wider text-primary-foreground disabled:opacity-50"
                >
                  {upsert.isPending ? 'Saving…' : 'Save'}
                </button>
              </>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
